import { useMemo } from "react";
import { flatten, isUndefined, map, sortBy } from "lodash";
import { DateMath, PeriodRange } from "@linkfluence/models/date";
import { useFacetValuesFromClause } from "@radarly/core/clause/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { Clause } from "@radarly/models/clause";
import { FullFacet, Value, getFacetValueLabel } from "@radarly/models/facets";
import {
  Choice,
  ClassificationFilter,
  ClassificationFilters,
  Status,
} from "@radarly/models/filters";
import { getStatus, orderChoices } from "./utils";

const CLASSIFICATION_LEVELS = 3;

const formatClassificationChoices = (
  values: Value[],
  facet: FullFacet,
  clause: Clause,
  order: Value[]
): Choice[] => {
  const ordering = orderChoices(order);
  return map(values, (value) => {
    const nodes = value.id.toString().split("/");
    const level = nodes.length;
    const parent = [...nodes].splice(0, level - 1).join("/");
    return {
      id: value.id,
      label: getFacetValueLabel(facet.id, value.label),
      status: getStatus(value.id, clause, facet),
      parent,
      position: !isUndefined(ordering[value.id])
        ? ordering[value.id]
        : order.length + 1,
    };
  });
};

export function useFilterbarClassificationChoices(
  clause: Clause,
  facet: FullFacet,
  period: PeriodRange = { from: DateMath.Last_30_Days, to: DateMath.Now }
) {
  const project = useCurrentProject();
  const { loading, values, order, error } = useFacetValuesFromClause(
    project.id,
    clause,
    facet,
    period
  );

  const checkNodeChildren = (
    level: number,
    levels: ClassificationFilters,
    id: string
  ): boolean => {
    if (level < CLASSIFICATION_LEVELS) {
      return levels[level + 1].some((child) => child.parent === id);
    }
    return false;
  };

  const getStatusFromChildrenNodes = (
    value: ClassificationFilter,
    level: number,
    nodes: ClassificationFilter[]
  ) => {
    if (value.status !== Status.None || level === CLASSIFICATION_LEVELS) {
      return value.status;
    }
    const node = nodes.find(
      (node) => node.id.includes(value.id) && node.status !== Status.None
    );
    return node ? node.status : Status.None;
  };

  const countSubfilters = (
    level: number,
    levels: ClassificationFilters,
    value: ClassificationFilter
  ) => {
    let included = 0;
    let excluded = 0;
    const count = (status: Status) => {
      return status === Status.Include
        ? included++
        : status === Status.Exclude
        ? excluded++
        : null;
    };
    if (level === 1) {
      levels[2].forEach((subfilter) => {
        if (subfilter.parent === value.id) {
          return count(subfilter.status);
        }
        return null;
      });
      levels[3].forEach((subfilter) => {
        if (subfilter.root === value.id) {
          return count(subfilter.status);
        }
        return null;
      });
    }
    if (level === 2) {
      levels[3].forEach((subfilter) => {
        if (subfilter.parent === value.id) {
          return count(subfilter.status);
        }
        return null;
      });
    }
    return {
      included,
      excluded,
    };
  };

  const choices = useMemo(() => {
    return formatClassificationChoices(values, facet, clause, order);
  }, [values, clause, facet, order]);

  const levelChoices = useMemo(() => {
    const levels: ClassificationFilters = { "1": [], "2": [], "3": [] };
    // format each value to match ClassificationFilter model
    values.forEach((value) => {
      const nodes = value.id.toString().split("/");
      const level = nodes.length;
      const _value = {
        id: value.id,
        label: value.label,
        status: getStatus(value.id, clause, facet),
        parent: [...nodes].splice(0, level - 1).join("/"),
        root: nodes[0],
      };
      // push the formatted value in the relevant level
      return levels[level].push(_value);
    });
    const allNodes = flatten(Object.values(levels));
    // loop over each level, check if a value has children nodes and infer its status from them
    Object.keys(levels).forEach((level) => {
      const _level = Number(level);
      levels[level].forEach((value) => {
        value.children = checkNodeChildren(_level, levels, value.id);
        value.status = getStatusFromChildrenNodes(value, _level, allNodes);
        value.subfilters = countSubfilters(_level, levels, value);
      });
    });
    return levels;
  }, [clause, facet, values]);

  return {
    loading,
    error,
    choices: sortBy(choices, "position"),
    levelChoices,
  };
}
